import { useCallback, useEffect, useRef, useState } from "react";

import Dropdown from "@/components/Dropdown";
import { listGrants, revokeGrant, shareWithTeam, shareWithUser } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import type { Access, Grant, Team } from "@/lib/types";

const ACCESS_CLS: Record<Access, string> = {
  write: "bg-green-500/20 text-green-400",
  read: "bg-black/10 text-text/60 dark:bg-white/10",
};

/** Small circled-i, matching the "What roles mean" affordance on the Teams page. */
function InfoIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

/**
 * Owner-only panel: share a private skill with teams and/or individual users (read/write).
 * `bare` drops the outer card chrome + heading (e.g. when rendered inside a modal that supplies them).
 * Two columns: current access (left, scrolls as the list grows) and an add-access form (right,
 * with an info toggle explaining what each access level means).
 */
export default function ShareSkill({ slug, myTeams, bare }: { slug: string; myTeams: Team[]; bare?: boolean }) {
  const { accessToken } = useAuth();
  const [grants, setGrants] = useState<Grant[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAccess, setShowAccess] = useState(false);
  const [listOpen, setListOpen] = useState(false); // "Has access" dropdown disclosure
  const accessInfoBtn = useRef<HTMLButtonElement>(null);

  // Close the "permission details" popover on any outside click or Escape
  // (clicking the toggle itself is handled by its own onClick).
  useEffect(() => {
    if (!showAccess) return;
    function onDown(e: MouseEvent) {
      if (accessInfoBtn.current?.contains(e.target as Node)) return;
      setShowAccess(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setShowAccess(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showAccess]);

  // add-access form (one row; the subject toggles between a team and a person)
  const [addKind, setAddKind] = useState<"team" | "user">("team");
  const [teamSlug, setTeamSlug] = useState("");
  const [email, setEmail] = useState("");
  const [access, setAccess] = useState<Access>("read");

  const load = useCallback(async () => {
    if (!accessToken) return;
    setGrants(await listGrants(slug, accessToken).catch(() => []));
  }, [slug, accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  async function run(fn: () => Promise<Grant[]>) {
    if (!accessToken) return;
    setBusy(true);
    setError(null);
    try {
      setGrants(await fn());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  const sharedTeamIds = new Set(grants.filter((g) => g.kind === "team").map((g) => g.id));
  const availableTeams = myTeams.filter((t) => !sharedTeamIds.has(t.id));

  // Current access is scoped to the selected kind (Teams vs Individuals), so the
  // list + avatar preview only ever show one kind at a time — never a stale mix.
  const shown = grants.filter((g) => g.kind === addKind);
  const noun = addKind === "team" ? (shown.length === 1 ? "team" : "teams") : shown.length === 1 ? "person" : "people";
  const previewGrants = shown.slice(0, 4);
  const extraCount = shown.length - previewGrants.length;
  const readCount = shown.filter((g) => g.access === "read").length;
  const writeCount = shown.length - readCount;
  const breakdown = [
    readCount ? `${readCount} read` : null,
    writeCount ? `${writeCount} write` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const addBtnCls = "shrink-0 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-40";

  const canAdd = addKind === "team" ? !!teamSlug : !!email.trim();
  function add() {
    setError(null);
    if (addKind === "team") {
      // teams already granted are filtered out of the picker, but guard anyway
      if (grants.some((g) => g.kind === "team" && g.slug === teamSlug)) {
        setListOpen(true);
        setError("That Team already has access.");
        return;
      }
      setListOpen(true);
      run(() => shareWithTeam(slug, teamSlug, access, accessToken!).then((g) => { setTeamSlug(""); return g; }));
    } else {
      const e = email.trim().toLowerCase();
      const already = grants.some((g) => g.kind === "user" && (g.detail ?? g.name).toLowerCase() === e);
      if (already) {
        setListOpen(true);
        setError("This User already has access.");
        return;
      }
      setListOpen(true);
      run(() => shareWithUser(slug, email.trim(), access, accessToken!).then((g) => { setEmail(""); return g; }));
    }
  }

  // Change an existing grant's access in place. Re-shares are upserts, so this just
  // updates the level (team keyed by slug, user by email — both already on the grant).
  function changeAccess(g: Grant, next: Access) {
    if (g.access === next) return;
    if (g.kind === "team") {
      if (g.slug) run(() => shareWithTeam(slug, g.slug!, next, accessToken!));
    } else if (g.detail) {
      run(() => shareWithUser(slug, g.detail!, next, accessToken!));
    }
  }

  return (
    <section className={bare ? "" : "mt-4 rounded-xl border border-black/10 bg-black/[0.02] p-4 dark:border-white/10 dark:bg-white/[0.02]"}>
      {!bare && <h2 className="font-display text-sm font-semibold text-text/80">Share access</h2>}

      <div className="mt-5 space-y-6">
        {/* ── Add collaborators (single horizontal row) ── */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-text/60">Add Collaborators</p>
            <div className="relative">
              <button
                ref={accessInfoBtn}
                type="button"
                onClick={() => setShowAccess((s) => !s)}
                aria-expanded={showAccess}
                className="inline-flex items-center gap-1 text-xs text-text/50 transition hover:text-text/80"
              >
                <InfoIcon /> View permission details
              </button>

              {showAccess && (
                <div
                  role="dialog"
                  className="absolute right-0 top-full z-30 mt-2 w-[280px] space-y-1.5 rounded-xl border border-[var(--border)] bg-[var(--bg-elev)] p-3 text-xs text-text/60"
                  style={{ boxShadow: "var(--shadow-card)" }}
                >
                  {/* caret — points up at the button so the popover reads as opening from it */}
                  <span className="absolute -top-1.5 right-4 h-3 w-3 rotate-45 border-l border-t border-[var(--border)] bg-[var(--bg-elev)]" />
                  <div className="flex items-center gap-2">
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${ACCESS_CLS.read}`}>read</span>
                    Can view, install this skill.
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${ACCESS_CLS.write}`}>write</span>
                    Can edit the skill, approve changes.
                  </div>
                  <p className="pt-1 text-text/40">Team members inherit the highest permission allowed by their team role.</p>
                </div>
              )}
            </div>
          </div>

          {/* Toggle · subject · access · action — all on one line, wraps on narrow widths */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canAdd && !busy) add();
            }}
            className="flex flex-wrap items-center gap-2"
          >
            <div className="inline-flex shrink-0 rounded-lg border border-black/10 bg-black/[0.06] p-0.5 text-sm dark:border-white/10 dark:bg-black/20">
              {(["team", "user"] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setAddKind(k)}
                  className={`rounded-md px-3 py-1.5 font-medium transition ${
                    addKind === k ? "bg-primary text-white" : "text-text/55 hover:text-text"
                  }`}
                >
                  {k === "team" ? "Teams" : "Individual"}
                </button>
              ))}
            </div>

            {addKind === "team" ? (
              <Dropdown
                value={teamSlug}
                onChange={setTeamSlug}
                ariaLabel="Team"
                placeholder="Choose a team"
                clearable
                searchable
                options={availableTeams.map((t) => ({ value: t.slug, label: t.name }))}
                className="min-w-[180px] flex-1"
              />
            ) : (
              <input
                type="email"
                placeholder="Search by name or email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="min-w-[180px] flex-1 rounded-lg border border-black/15 bg-black/[0.03] px-3 py-2 text-sm outline-none transition focus:border-primary dark:border-white/15 dark:bg-white/5"
              />
            )}

            <Dropdown
              value={access}
              onChange={(v) => setAccess(v as Access)}
              ariaLabel="Access level"
              options={[
                { value: "read", label: "Read" },
                { value: "write", label: "Write" },
              ]}
              className="w-32 shrink-0"
            />
            <button type="submit" disabled={busy || !canAdd} className={addBtnCls}>
              Grant Access
            </button>
          </form>

          {addKind === "team" && availableTeams.length === 0 && (
            <p className="mt-2 text-xs text-text/40">
              {myTeams.length === 0 ? "You're not on any teams yet." : "All your teams already have access."}
            </p>
          )}

          {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
        </div>

        {/* ── Current access (full width) ── */}
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text/60">
            Current Access · {addKind === "team" ? "Teams" : "Individuals"}
          </p>

          {shown.length === 0 ? (
            <div className="rounded-lg border border-dashed border-black/15 px-3 py-6 text-center text-xs text-text/40 dark:border-white/12">
              No {addKind === "team" ? "teams" : "individuals"} have access yet.
.            </div>
          ) : (
            <div>
              {/* Summary trigger — collapses a growing list into an avatar stack + count */}
              <button
                type="button"
                onClick={() => setListOpen((o) => !o)}
                aria-expanded={listOpen}
                className="flex w-full items-center gap-3 rounded-lg border border-[var(--border)] bg-[var(--bg-elev)] px-3 py-2 text-left outline-none transition-colors hover:border-primary/40 focus:border-primary"
              >
                <div className="flex -space-x-2">
                  {previewGrants.map((g) => (
                    <span
                      key={`${g.kind}:${g.id}`}
                      className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-accent ring-2 ring-[var(--bg-elev)]"
                    >
                      {g.kind === "team" ? (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      ) : (
                        g.name.charAt(0).toUpperCase()
                      )}
                    </span>
                  ))}
                  {extraCount > 0 && (
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/10 text-[11px] font-semibold text-text/55 ring-2 ring-[var(--bg-elev)] dark:bg-white/15">
                      +{extraCount}
                    </span>
                  )}
                </div>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-text/80">{shown.length} {noun} with access</span>
                  {breakdown && <span className="block text-xs text-text/45">{breakdown}</span>}
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`shrink-0 text-text/40 transition-transform duration-150 ${listOpen ? "rotate-180" : ""}`}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {listOpen && (
                <ul
                  className="mt-1.5 max-h-[300px] space-y-1 overflow-y-auto rounded-lg border border-[var(--border)] bg-[var(--bg-elev)] p-1.5"
                  style={{ boxShadow: "var(--shadow-card)" }}
                >
                  {shown.map((g) => (
                    <li
                      key={`${g.kind}:${g.id}`}
                      className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-xs font-semibold text-accent">
                        {g.kind === "team" ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                        ) : (
                          g.name.charAt(0).toUpperCase()
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{g.name}</span>
                        {g.detail && <span className="block truncate text-xs text-text/40">{g.detail}</span>}
                      </span>
                      <div className="inline-flex shrink-0 rounded-md border border-[var(--border)] p-0.5 text-[11px] font-medium">
                        {(["read", "write"] as Access[]).map((a) => (
                          <button
                            key={a}
                            type="button"
                            onClick={() => changeAccess(g, a)}
                            disabled={busy}
                            aria-pressed={g.access === a}
                            title={`Set ${a} access`}
                            className={`rounded px-2 py-0.5 capitalize transition disabled:opacity-50 ${
                              g.access === a
                                ? a === "write"
                                  ? "bg-green-500/20 text-green-400"
                                  : "bg-black/10 text-text/70 dark:bg-white/15"
                                : "text-text/40 hover:text-text/70"
                            }`}
                          >
                            {a}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => run(() => revokeGrant(slug, g.kind, g.id, accessToken!))}
                        disabled={busy}
                        aria-label="Remove"
                        title="Remove access"
                        className="shrink-0 rounded-md p-1 text-text/40 transition hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
